:host { all: initial; display: block; }
* { box-sizing: border-box; }

.vma {
  container-type: inline-size;
  /* Standalone palette (OKLCH, neutrals tinted navy) — light theme. */
  --navy: oklch(0.26 0.10 282);
  --teal: oklch(0.78 0.07 195);
  --bg: oklch(0.99 0.004 280);
  --surface: oklch(0.975 0.006 280);
  --surface-2: oklch(0.955 0.008 280);
  --text: oklch(0.28 0.03 282);
  --muted: oklch(0.52 0.02 282);
  --border: oklch(0.90 0.012 280);
  --ring: oklch(0.72 0.09 195);
  /* Threat-level fills (deep enough for white text). */
  --lvl-0: oklch(0.5 0.02 280);
  --lvl-1: oklch(0.52 0.14 150);
  --lvl-2: oklch(0.56 0.13 80);
  --lvl-3: oklch(0.56 0.18 48);
  --lvl-4: oklch(0.5 0.21 27);
  font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  font-size: 15px;
  line-height: 1.5;
  color: var(--text);
  -webkit-font-smoothing: antialiased;
}
.vma[data-theme='dark'] {
  --bg: oklch(0.205 0.022 282);
  --surface: oklch(0.245 0.024 282);
  --surface-2: oklch(0.285 0.026 282);
  --text: oklch(0.93 0.01 280);
  --muted: oklch(0.72 0.015 280);
  --border: oklch(0.34 0.022 282);
}

.vma__bar {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}
.vma__title { margin: 0; font-size: 1.25rem; font-weight: 700; letter-spacing: -0.01em; color: var(--text); }
.vma__src { margin: 0.3rem 0 0; font-size: 0.85rem; color: var(--muted); }
.vma__src a { color: inherit; }

.vma__filters { display: flex; gap: 0.4rem; flex-wrap: wrap; }
.vma__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.7rem;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--muted);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 999px;
  cursor: pointer;
  transition: border-color 0.18s, color 0.18s, background-color 0.18s;
}
.vma__chip:hover { border-color: var(--ring); }
.vma__chip.is-active { color: var(--text); border-color: var(--ring); background: oklch(0.72 0.09 195 / 0.16); }
.vma__dot { width: 0.55rem; height: 0.55rem; border-radius: 50%; }
.vma__dot[data-l='1'] { background: var(--lvl-1); }
.vma__dot[data-l='2'] { background: var(--lvl-2); }
.vma__dot[data-l='3'] { background: var(--lvl-3); }
.vma__dot[data-l='4'] { background: var(--lvl-4); }

.vma__status { color: var(--muted); font-size: 0.95rem; padding: 1rem 0; }
.vma__status.is-error { color: var(--lvl-4); }

.vma__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.85rem;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 21rem), 1fr));
}

.vma__item {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  padding: 1rem 1.1rem;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 0.85rem;
  animation: vma-in 0.4s cubic-bezier(0.16, 1, 0.3, 1) both;
}
@keyframes vma-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { .vma__item { animation: none; } }

.vma__head { display: flex; align-items: center; gap: 0.6rem; min-width: 0; }
.vma__flag {
  flex: none;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 0 0 1px var(--border);
}
.vma__flag--none { display: inline-block; background: var(--surface-2); }
.vma__country {
  font-weight: 600;
  font-size: 1.05rem;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.vma__date { flex: none; margin-left: auto; font-size: 0.75rem; color: var(--muted); }

/* The threat level is the hero — filled two-tone pill, colour-coded. */
.vma__level {
  display: inline-flex;
  align-self: flex-start;
  align-items: stretch;
  border-radius: 0.5rem;
  overflow: hidden;
  font-weight: 700;
  line-height: 1.2;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.14);
}
.vma__level-n {
  display: flex;
  align-items: center;
  padding: 0.36rem 0.55rem;
  font-size: 0.72rem;
  letter-spacing: 0.03em;
  color: #fff;
  background: color-mix(in oklab, var(--fill) 84%, black);
}
.vma__level-label {
  display: flex;
  align-items: center;
  padding: 0.36rem 0.75rem;
  font-size: 0.95rem;
  color: #fff;
  background: var(--fill);
}

/* Per-ministry breakdown: mini flag + origin code + its normalized level. */
.vma__origins { display: flex; flex-wrap: wrap; gap: 0.35rem; }
.vma__origin {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.14rem 0.3rem 0.14rem 0.2rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--muted);
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 999px;
  text-decoration: none;
  transition: border-color 0.18s, color 0.18s;
}
a.vma__origin:hover { border-color: var(--ring); color: var(--text); }
.vma__origin img { width: 16px; height: 16px; border-radius: 50%; object-fit: cover; }
.vma__origin-l {
  min-width: 1.05rem;
  text-align: center;
  border-radius: 999px;
  padding: 0 0.28rem;
  color: #fff;
  font-size: 0.68rem;
  background: var(--fill, var(--lvl-0));
}
.vma__consensus {
  display: inline-flex;
  align-items: center;
  padding: 0.14rem 0.5rem;
  font-size: 0.72rem;
  font-weight: 700;
  color: #fff;
  background: var(--navy);
  border-radius: 999px;
}
.vma[data-theme='dark'] .vma__consensus { background: var(--teal); color: var(--navy); }

.vma__summary {
  margin: 0;
  font-size: 0.86rem;
  line-height: 1.5;
  color: var(--muted);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.vma__summary--synthesis {
  -webkit-line-clamp: 4;
  color: var(--text);
}
.vma__read {
  align-self: flex-start;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--navy);
  text-decoration: none;
}
.vma[data-theme='dark'] .vma__read { color: var(--teal); }
.vma__read:hover { text-decoration: underline; }
